// httpPorts — the Project slice: the author's projects over the command
// bus, and the parse of one `/state` project at the boundary.

import { Result } from "@bilda/utils";
import type { ExternalError } from "../externalError.js";
import { mkMalformed } from "../externalError.js";
import type {
    ProjectCanvasItem,
    ProjectFormat,
    ProjectItem,
    ProjectPorts,
} from "../ports/project.js";
import { parseProjectIssues } from "../projectIssuesAdapter.js";
import { parseProjectObjectives } from "../projectObjectivesAdapter.js";
import { fields, list, num, str } from "../wireShape.js";
import { command } from "./shared.js";

const done = async (r: Promise<Result.Result<unknown, ExternalError>>) => {
    const settled = await r;
    return Result.isErr(settled) ? settled : Result.ok(undefined);
};

const parseCanvasItem = (raw: unknown): ProjectCanvasItem[] => {
    const f = fields(raw);
    const target = str(f?.get("target"));
    const x = num(f?.get("x"));
    const y = num(f?.get("y"));
    return target === undefined || x === undefined || y === undefined
        ? []
        : [{ target, x, y }];
};

// A project's format; one the client cannot read is none.
const parseFormat = (raw: unknown): ProjectFormat | undefined => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    const platform = str(f?.get("platform"));
    return type === "no_fixed_format"
        ? { type }
        : type === "course" && platform !== undefined
          ? { type, platform }
          : undefined;
};

// One `/state` project: a row without an id or title drops; an unknown
// lifecycle reads as active; a malformed canvas item or audience drops; no
// description reads as ""; an unreadable format as none.
export const parseProject = (raw: unknown): ProjectItem[] => {
    const f = fields(raw);
    const id = str(f?.get("id"));
    const title = str(f?.get("title"));
    if (id === undefined || title === undefined) return [];
    return [
        {
            id,
            title,
            description: str(f?.get("description")) ?? "",
            ...((format) => (format === undefined ? {} : { format }))(
                parseFormat(f?.get("format")),
            ),
            audiences: list(f?.get("audiences")).flatMap((a) => {
                const id = str(a);
                return id === undefined ? [] : [id];
            }),
            lifecycle:
                str(f?.get("lifecycle")) === "archived" ? "archived" : "active",
            canvas: list(f?.get("canvas")).flatMap(parseCanvasItem),
        },
    ];
};

export const projectHttp: ProjectPorts = {
    createProject: async (title, start, details) => {
        const r = await command("createProject", {
            title,
            ...(start.modules.length === 0 ? {} : { modules: start.modules }),
            ...details,
        });
        if (Result.isErr(r)) return r;
        const projectId = str(fields(r.value)?.get("projectId"));
        return projectId === undefined
            ? Result.err(mkMalformed("createProject returned no project id"))
            : Result.ok(projectId);
    },
    renameProject: (projectId, title) =>
        done(command("renameProject", { projectId, title })),
    setProjectDescription: (projectId, description) =>
        done(command("setProjectDescription", { projectId, description })),
    clearProjectDescription: (projectId) =>
        done(command("clearProjectDescription", { projectId })),
    addAudienceToProject: (projectId, audience) =>
        done(command("addAudienceToProject", { projectId, audience })),
    removeAudienceFromProject: (projectId, audience) =>
        done(command("removeAudienceFromProject", { projectId, audience })),
    archiveProject: (projectId) =>
        done(command("archiveProject", { projectId })),
    restoreProject: (projectId) =>
        done(command("restoreProject", { projectId })),
    removeProject: (projectId) => done(command("removeProject", { projectId })),
    placeOnProjectCanvas: (projectId, target, at) =>
        done(command("placeOnProjectCanvas", { projectId, target, at })),
    moveOnProjectCanvas: (projectId, target, at) =>
        done(command("moveOnProjectCanvas", { projectId, target, at })),
    takeOffProjectCanvas: (projectId, target) =>
        done(command("takeOffProjectCanvas", { projectId, target })),
    addObjectiveToProject: (projectId, statement) =>
        done(command("addObjectiveToProject", { projectId, statement })),
    removeObjectiveFromProject: (projectId, objective) =>
        done(command("removeObjectiveFromProject", { projectId, objective })),
    loadProjectObjectives: async (projectId) => {
        const r = await command("projectObjectives", { projectId });
        return Result.isErr(r) ? r : parseProjectObjectives(r.value);
    },
    loadProjectIssues: async (projectId) => {
        const r = await command("projectIssues", { projectId });
        return Result.isErr(r) ? r : parseProjectIssues(r.value);
    },
};
